import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Utensils, Car, TreePine } from "lucide-react";

// Reference point aus Kunden-Vorgabe:
// 10.000 € nachhaltige Anlage sparen ~1.500 kg CO2/Jahr vs. konventionell.
// Äquivalente pro 10.000 €:  12 Monate vegan · 8.500 km Auto · 120 Bäume
// -> abgeleitete Koeffizienten (kg CO2 pro Einheit):
const CO2_PER_EURO = 0.15;         // kg CO2 / Jahr pro €
const VEGAN_KG_PER_MONTH = 125;    // 1500 / 12
const CAR_KG_PER_KM = 1500 / 8500; // ≈ 0.17647, damit 10.000 € genau 8.500 km ergibt
const TREE_KG_PER_YEAR = 12.5;     // 1500 / 120 (Buche mittelalt, pro Jahr)

function fmt(n) {
  return Math.round(n).toLocaleString("de-DE");
}

export default function GreenLeverCalculator() {
  const [amount, setAmount] = useState(10000);

  const { co2, veganMonths, carKm, trees } = useMemo(() => {
    const co2 = amount * CO2_PER_EURO;
    return {
      co2,
      veganMonths: co2 / VEGAN_KG_PER_MONTH,
      carKm: co2 / CAR_KG_PER_KM,
      trees: co2 / TREE_KG_PER_YEAR,
    };
  }, [amount]);

  let veganLabel;
  if (veganMonths > 12) {
    const years = veganMonths / 12;
    const yearsStr = Number.isInteger(years)
      ? `${years}`
      : years.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    veganLabel = `${yearsStr} Jahre`;
  } else {
    veganLabel = `${fmt(veganMonths)} Monate`;
  }

  const equivalents = [
    {
      icon: Utensils,
      value: veganLabel,
      label: "vegane Ernährung",
      detail: `entspricht dem Verzicht auf tierische Produkte über ${veganLabel}`,
      testid: "eq-vegan",
    },
    {
      icon: Car,
      value: `${fmt(carKm)} km`,
      label: "Autoverzicht",
      detail: `entspricht der Einsparung von ${fmt(carKm)} nicht gefahrenen Kilometern`,
      testid: "eq-car",
    },
    {
      icon: TreePine,
      value: `${fmt(trees)} Bäume`,
      label: "Baumpflanzung",
      detail: `entspricht der jährlichen CO₂-Aufnahme von ${fmt(trees)} neu gepflanzten Bäumen`,
      testid: "eq-trees",
    },
  ];

  return (
    <div className="border border-neutral-200 bg-white p-8 md:p-10" data-testid="green-lever-calculator">
      <div className="overline mb-3">Rechner · Impact</div>
      <h3 className="font-serif text-3xl md:text-4xl font-medium tracking-tight mb-2">Grüne Hebel-Simulator</h3>
      <p className="text-neutral-600 mb-8 max-w-3xl">
        Der wahre Hebel Ihres Geldes: Ein einziger Euro in einer nachhaltigen Anlage wirkt oft
        stärker als monatelanges Umsteuern im Alltag. Geben Sie Ihre Anlagesumme ein –
        der Rechner übersetzt die jährliche CO₂-Ersparnis in greifbare Vergleiche.
      </p>

      <div className="grid md:grid-cols-2 gap-10 items-start">
        <div className="space-y-6">
          <div>
            <label className="text-xs uppercase tracking-wider text-neutral-500 block mb-2">Anlagesumme</label>
            <Input
              type="number"
              min={0}
              value={amount}
              onChange={(e) => setAmount(Math.max(0, parseInt(e.target.value) || 0))}
              className="rounded-none font-mono text-2xl h-14"
              data-testid="lever-input-amount"
            />
          </div>
          <div>
            <div className="flex justify-between mb-2">
              <label className="text-sm font-medium text-neutral-700">Schnellauswahl</label>
              <span className="font-mono text-sm text-brand-primary">{fmt(amount)} €</span>
            </div>
            <Slider
              value={[amount]}
              min={500}
              max={200000}
              step={500}
              onValueChange={(v) => setAmount(v[0])}
              data-testid="lever-slider-amount"
            />
            <div className="flex justify-between text-xs text-neutral-500 mt-2 font-mono">
              <span>500 €</span><span>200.000 €</span>
            </div>
          </div>

          <div className="border border-brand-primary bg-brand-primary text-white p-6 mt-8">
            <div className="text-xs uppercase tracking-[0.2em] text-white/80 mb-2">Jährliche CO₂-Ersparnis</div>
            <div className="font-mono text-4xl md:text-5xl leading-none" data-testid="lever-co2">
              {fmt(co2)} <span className="text-2xl md:text-3xl text-white/80">kg</span>
            </div>
            <div className="text-sm text-white/80 mt-3">
              gegenüber einer konventionellen Anlage gleicher Höhe
            </div>
          </div>
        </div>

        <div>
          <div className="text-sm text-neutral-600 mb-4">Ihre Anlage spart so viel CO₂ ein wie …</div>
          <div className="space-y-4">
            {equivalents.map(({ icon: Icon, value, label, detail, testid }) => (
              <div key={label} className="flex items-start gap-4 border border-neutral-200 p-5 bg-brand-paper" data-testid={testid}>
                <div className="w-12 h-12 shrink-0 bg-white border border-neutral-200 flex items-center justify-center">
                  <Icon strokeWidth={1.5} size={22} className="text-brand-primary" />
                </div>
                <div>
                  <div className="font-mono text-xl text-brand-dark">{value}</div>
                  <div className="text-sm font-medium text-neutral-800 mt-0.5">{label}</div>
                  <div className="text-xs text-neutral-500 mt-1 leading-relaxed">{detail}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <p className="text-xs text-neutral-500 mt-8 border-t border-neutral-100 pt-6">
        Modellrechnung auf Basis empirischer Durchschnittswerte: 1 € nachhaltig angelegt spart
        rechnerisch ca. 0,15 kg CO₂e pro Jahr (Referenz: 10.000 € ≈ 1,5 t CO₂/Jahr). Vegan =
        1.500 kg CO₂e/Jahr gegenüber Standard-Mischkost; Auto = 176 g CO₂/km (deutscher
        PKW-Durchschnitt); Baum = 12,5 kg CO₂ Netto-Aufnahme pro Jahr. Keine Anlageempfehlung.
      </p>
    </div>
  );
}
